/** Typography (docs/06-design-system.md › Typography). Mobile-first: base sizes are phone sizes. */
export const fontFamily = {
  display: '"Fredoka", "Baloo 2", ui-rounded, "SF Pro Rounded", system-ui, sans-serif',
  body: '"Nunito", ui-rounded, "SF Pro Rounded", system-ui, -apple-system, "Segoe UI", sans-serif',
  mono: 'ui-monospace, "SF Mono", Menlo, monospace',
} as const;

export const fontWeight = { regular: 400, semibold: 600, bold: 700, extrabold: 800 } as const;

/** Google Fonts stylesheet for exactly the weights we use. */
export const googleFontsHref =
  'https://fonts.googleapis.com/css2?family=Fredoka:wght@300..700&family=Nunito:wght@400;600;700;800&display=swap';

type TextStyle = {
  size: string;
  /** Size from the md breakpoint (768px) up, if different. */
  sizeMd?: string;
  lineHeight: number;
  weight: number;
  family: keyof typeof fontFamily;
  letterSpacing?: string;
  usage: string;
};

export const textStyles = {
  display: {
    size: '2.25rem',
    sizeMd: '3rem',
    lineHeight: 1.1,
    weight: 600,
    family: 'display',
    usage: 'Onboarding hero, celebrations',
  },
  h1: {
    size: '1.75rem',
    sizeMd: '2.125rem',
    lineHeight: 1.15,
    weight: 600,
    family: 'display',
    usage: 'Page titles',
  },
  h2: {
    size: '1.375rem',
    sizeMd: '1.5rem',
    lineHeight: 1.2,
    weight: 600,
    family: 'display',
    usage: 'Section titles',
  },
  h3: {
    size: '1.125rem',
    sizeMd: '1.25rem',
    lineHeight: 1.3,
    weight: 800,
    family: 'body',
    usage: 'Card titles, recipient names',
  },
  bodyLg: {
    size: '1.125rem',
    lineHeight: 1.5,
    weight: 400,
    family: 'body',
    usage: 'Empty-state copy',
  },
  body: {
    size: '1rem',
    lineHeight: 1.5,
    weight: 400,
    family: 'body',
    usage: 'Default. Never smaller for primary content on mobile.',
  },
  bodySm: {
    size: '0.875rem',
    lineHeight: 1.45,
    weight: 600,
    family: 'body',
    usage: 'Meta, secondary',
  },
  label: {
    size: '0.9375rem',
    lineHeight: 1.2,
    weight: 700,
    family: 'body',
    usage: 'Form labels, buttons',
  },
  caption: {
    size: '0.75rem',
    lineHeight: 1.35,
    weight: 700,
    family: 'body',
    letterSpacing: '0.02em',
    usage: 'Tags, badges',
  },
  code: {
    size: '1.5rem',
    sizeMd: '1.75rem',
    lineHeight: 1.1,
    weight: 600,
    family: 'display',
    letterSpacing: '0.1em',
    usage: 'Join code',
  },
} as const satisfies Record<string, TextStyle>;

export type TextStyleName = keyof typeof textStyles;
